import React, { useEffect, useState } from 'react';
import { Box, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { MapContainer, TileLayer, Marker, Polygon, Polyline, LayersControl, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SearchableSelect from '../inputs/SearchableSelect';
import { getProvinces, getDistricts, getNeighborhoods, getStreets, getBuildings } from '../../services/tkgm';

// Leaflet default marker icon fix for bundlers
try {
  // @ts-ignore
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  });
} catch (_) { }

// Koordinatları Leaflet formatına [lat, lng] çeviren yardımcı fonksiyon
const formatCoords = (coords, type) => {
  if (type === "Polygon") {
    return coords[0].map(item => [item[1], item[0]]); // [lng, lat] -> [lat, lng]
  }
  if (type === "MultiLineString") {
    return coords.map(line => line.map(item => [item[1], item[0]]));
  }
  if (type === "Point") {
    return [coords[1], coords[0]];
  }
  return [];
};

const flattenLines = (lines) => {
  try {
    return (lines || []).reduce((acc, line) => acc.concat(line || []), []);
  } catch (_) {
    return [];
  }
};

export default function MaksMapBox({ onSelect, onProvinceChange }) {
  const [loading, setLoading] = useState(false);
  const [mapData, setMapData] = useState({
    iller: [], ilceler: [], mahalleler: [], sokaklar: [], binalar: []
  });
  const [selection, setSelection] = useState({
    il: '', ilce: '', mahalle: '', sokak: '', bina: ''
  });
  const [geoJson, setGeoJson] = useState({
    activePolygon: null, activeLines: [], activePoint: null
  });

  const reverseGeocode = async (lat, lng) => {
    try {
      const qs = new URLSearchParams({
        format: 'jsonv2',
        lat: String(lat),
        lon: String(lng),
        zoom: '18',
        addressdetails: '1',
        'accept-language': 'tr',
      });
      const resp = await fetch(`https://nominatim.openstreetmap.org/reverse?${qs.toString()}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!resp.ok) return null;
      return await resp.json();
    } catch (_) {
      return null;
    }
  };

  function MapClickSelect() {
    useMapEvents({
      click: async (evt) => {
        try {
          const lat = evt?.latlng?.lat;
          const lng = evt?.latlng?.lng;
          if (typeof lat !== 'number' || typeof lng !== 'number') return;
          const point = [lat, lng];
          setGeoJson((prev) => ({ ...prev, activePoint: point }));
          setLoading(true);
          const geo = await reverseGeocode(lat, lng);
          const addr = geo?.display_name || '';
          const a = geo?.address || {};
          const province = a?.city || a?.town || a?.village || a?.state || a?.province || '';
          const district = a?.county || a?.state_district || a?.district || '';
          const neighborhood = a?.neighbourhood || a?.suburb || a?.quarter || a?.city_district || '';
          const street = a?.road || '';
          const building = a?.house_number || '';
          try {
            if (onProvinceChange && province) onProvinceChange(province);
          } catch (_) { }
          if (onSelect) {
            onSelect({
              address: addr || `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
              lat,
              lng,
              province,
              district,
              neighborhood,
              street,
              building,
            });
          }
        } finally {
          setLoading(false);
        }
      },
    });
    return null;
  }

  // 1. İlleri Yükle (backend proxy üzerinden)
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const items = await getProvinces();
        setMapData(prev => ({ ...prev, iller: items || [] }));
      } catch (err) { console.error("Veri çekme hatası:", err); }
      setLoading(false);
    })();
  }, []);

  // Seçim Değişiklikleri
  const handleSelect = (id, level) => {
    const keyMap = { il: 'iller', ilce: 'ilceler', mahalle: 'mahalleler', sokak: 'sokaklar', bina: 'binalar' };
    const key = keyMap[level];
    const list = (mapData[key] || []);
    const selectedFeature = list.find(f => f?.id === id);

    const nextSel = { ...selection, [level]: id };
    setSelection(nextSel);

    if (selectedFeature) {
      const geom = selectedFeature.geometry || {};
      const type = geom.type;
      const coordinates = geom.coordinates;

      // Harita görselleştirmesini güncelle
      if (type === "Polygon") {
        setGeoJson({ activePolygon: formatCoords(coordinates, "Polygon"), activeLines: [], activePoint: null });
      } else if (type === "MultiLineString") {
        setGeoJson(prev => ({ ...prev, activeLines: formatCoords(coordinates, "MultiLineString"), activePoint: null }));
      } else if (type === "Point") {
        setGeoJson(prev => ({ ...prev, activePoint: formatCoords(coordinates, "Point") }));
      }

      // Alt seviye verileri çek
      if (level === 'il') {
        (async () => {
          setLoading(true);
          try {
            const items = await getDistricts(id);
            setMapData(prev => ({ ...prev, ilceler: items || [], mahalleler: [], sokaklar: [], binalar: [] }));
          } catch (err) { console.error("İlçe verisi çekme hatası:", err); }
          setLoading(false);
        })();
        setMapData(prev => ({ ...prev, mahalleler: [], sokaklar: [], binalar: [] }));
        setSelection(prev => ({ ...prev, ilce: '', mahalle: '', sokak: '', bina: '' }));
        try {
          const ilText = selectedFeature?.text || "";
          if (onProvinceChange && ilText) onProvinceChange(ilText, id);
        } catch (_) { }
      }
      if (level === 'ilce') {
        (async () => {
          setLoading(true);
          try {
            const items = await getNeighborhoods(id);
            setMapData(prev => ({ ...prev, mahalleler: items || [], sokaklar: [], binalar: [] }));
          } catch (err) { console.error("Mahalle verisi çekme hatası:", err); }
          setLoading(false);
        })();
        setMapData(prev => ({ ...prev, sokaklar: [], binalar: [] }));
        setSelection(prev => ({ ...prev, mahalle: '', sokak: '', bina: '' }));
      }
      if (level === 'mahalle') {
        (async () => {
          setLoading(true);
          try {
            const items = await getStreets(id);
            setMapData(prev => ({ ...prev, sokaklar: items || [], binalar: [] }));
          } catch (err) { console.error("Sokak verisi çekme hatası:", err); }
          setLoading(false);
        })();
        setMapData(prev => ({ ...prev, binalar: [] }));
        setSelection(prev => ({ ...prev, sokak: '', bina: '' }));
      }
      if (level === 'sokak') {
        (async () => {
          setLoading(true);
          try {
            const items = await getBuildings(id);
            setMapData(prev => ({ ...prev, binalar: items || [] }));
          } catch (err) { console.error("Bina verisi çekme hatası:", err); }
          setLoading(false);
        })();
        setSelection(prev => ({ ...prev, bina: '' }));
      }
      if (level === 'bina') {
        // Compose address and emit selection
        const getText = (arr, selId) => (arr || []).find(f => f?.id === selId)?.text || "";
        const ilText = getText(mapData.iller, nextSel.il);
        const ilceText = getText(mapData.ilceler, nextSel.ilce);
        const mahText = getText(mapData.mahalleler, nextSel.mahalle);
        const sokText = getText(mapData.sokaklar, nextSel.sokak);
        const binaItem = (mapData.binalar || []).find(f => f?.id === nextSel.bina);
        const binaText = binaItem?.text || "";
        const point = binaItem?.coords && binaItem.coords.length === 2 ? [binaItem.coords[0], binaItem.coords[1]] : (type === "Point" ? formatCoords(coordinates, "Point") : geoJson.activePoint);
        if (point && point.length === 2) {
          setGeoJson(prev => ({ ...prev, activePoint: point }));
        }
        const addr = [sokText, binaText ? `No: ${binaText.replace(/\D+/g, '') || binaText}` : null, mahText, ilceText, ilText]
          .filter(Boolean).join(", ");
        if (onSelect && point) {
          onSelect({
            address: addr,
            lat: point[0],
            lng: point[1],
            province: ilText,
            district: ilceText,
            neighborhood: mahText,
            street: sokText,
            building: binaText,
          });
        }
      }
    }
  };

  function RecenterMap({ bounds }) {
    const map = useMap();
    useEffect(() => {
      let cancelled = false;
      const doFit = () => {
        if (cancelled) return;
        if (!bounds || bounds.length === 0) return;
        try {
          map.fitBounds(bounds, { padding: [20, 20], animate: false });
        } catch (_) {
          /* noop */
        }
      };
      if (map && typeof map.whenReady === "function") {
        map.whenReady(doFit);
      } else {
        doFit();
      }
      return () => {
        cancelled = true;
      };
    }, [map, JSON.stringify(bounds)]);
    return null;
  }

  return (
    <VStack spacing={4} align="stretch" w="full">
      <Box p={4} bg="gray.50" borderRadius="md" shadow="sm">
        <HStack spacing={3} wrap="wrap">
          <SearchableSelect
            items={(mapData.iller || []).map(f => ({ value: f.id, label: f.text }))}
            value={selection.il}
            onChange={(val) => handleSelect(val, 'il')}
            placeholder="İl Seçiniz"
          />
          <SearchableSelect
            items={(mapData.ilceler || []).map(f => ({ value: f.id, label: f.text }))}
            value={selection.ilce}
            onChange={(val) => handleSelect(val, 'ilce')}
            placeholder="İlçe Seçiniz"
            disabled={!selection.il}
          />
          <SearchableSelect
            items={(mapData.mahalleler || []).map(f => ({ value: f.id, label: f.text }))}
            value={selection.mahalle}
            onChange={(val) => handleSelect(val, 'mahalle')}
            placeholder="Mahalle Seçiniz"
            disabled={!selection.ilce}
          />
          <SearchableSelect
            items={(mapData.sokaklar || []).map(f => ({ value: f.id, label: f.text }))}
            value={selection.sokak}
            onChange={(val) => handleSelect(val, 'sokak')}
            placeholder="Sokak Seçiniz"
            disabled={!selection.mahalle}
          />
          <SearchableSelect
            items={(mapData.binalar || []).map(f => ({ value: f.id, label: f.text }))}
            value={selection.bina}
            onChange={(val) => handleSelect(val, 'bina')}
            placeholder="No Seçiniz"
            disabled={!selection.sokak}
          />
          {loading && <Spinner color="red.500" />}
        </HStack>
      </Box>

      <Box height="500px" borderRadius="lg" overflow="hidden" border="1px solid #ccc">
        <MapContainer center={[39.9334, 32.8597]} zoom={6} style={{ height: '100%', width: '100%' }}>
          <MapClickSelect />
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Uydu (Esri)">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution="Tiles &copy; Esri — Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="OpenStreetMap">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap contributors"
              />
            </LayersControl.BaseLayer>
          </LayersControl>

          {/* İl/İlçe/Mahalle Sınırı */}
          {geoJson.activePolygon && (
            <>
              <Polygon positions={geoJson.activePolygon} pathOptions={{ color: 'red', fillColor: 'red', fillOpacity: 0.1, weight: 2 }} />
              <RecenterMap bounds={geoJson.activePolygon} />
            </>
          )}

          {/* Sokak Çizgileri */}
          {geoJson.activeLines.length > 0 && (
            <>
              <Polyline positions={geoJson.activeLines} pathOptions={{ color: 'blue', weight: 4 }} />
              <RecenterMap bounds={flattenLines(geoJson.activeLines)} />
            </>
          )}

          {/* Bina Noktası */}
          {geoJson.activePoint && (
            <>
              <Marker position={geoJson.activePoint} />
              <RecenterMap bounds={[geoJson.activePoint]} />
            </>
          )}
        </MapContainer>
      </Box>
    </VStack>
  );
}
